export type CurrencyCellValue = string | number | null | undefined;

function isBlankCurrencyValue(value: CurrencyCellValue): boolean {
  return (
    value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
  );
}

export function toCurrencyCsvCell(value: CurrencyCellValue): string {
  return value === null || value === undefined ? '' : `${value}`;
}

export function toCurrencyInputModelValue(value: CurrencyCellValue): string | number {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string' && value.trim() === '') {
    return '';
  }
  return value;
}

function canonicalAmount(value: CurrencyCellValue): string {
  if (isBlankCurrencyValue(value)) return '';
  let text = `${value}`.trim();
  if (text.includes('.')) text = text.replace(/0+$/, '').replace(/\.$/, '');
  return text === '-0' ? '0' : text;
}

/**
 * Whether an amount the editor hands back is the row's amount already, only
 * written differently (`18.5` for `18.50`). The currency editor normalises its
 * text on every focus loss — also when Escape discarded the typing (I-2833) —
 * and the cell must not save an amount nobody changed.
 */
export function isSameCurrencyAmount(a: CurrencyCellValue, b: CurrencyCellValue): boolean {
  return canonicalAmount(a) === canonicalAmount(b);
}

export function formatCurrencyCell(
  value: CurrencyCellValue,
  formatter: Pick<Intl.NumberFormat, 'format'>
): string {
  if (isBlankCurrencyValue(value)) {
    return '';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '';
  }

  return formatter.format(numericValue);
}

/**
 * Splits a formatted currency total („580,00 €", „+1.234,50 CHF", „$580.00")
 * into the amount and the currency label, so the summary row lays them out
 * like the body cells: the amount right-aligned before a reserved label slot
 * (BLP/nx#3200 — „580,00 €" stood shifted under the column). Null when the
 * text carries no digit or no label (a placeholder such as „—").
 */
export function splitCurrencyText(text: string): { amount: string; label: string } | null {
  if (!/\d/.test(text)) return null;
  const label = text.replace(/[\d\s.,'+\-−  ]/g, '');
  if (label === '') return null;
  const amount = text
    .replace(label, '')
    .replace(/[\s  ]+/g, ' ')
    .trim();
  return amount === '' ? null : { amount, label };
}
